import { useCallback, useState, useMemo } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { SideDrawer } from '@antimatterhq/ui/components/sideDrawer';
import { JsonEditor } from '@antimatterhq/ui/components/jsonEditor';
import { useColorMode } from '@antimatterhq/ui/contexts/ColorModeContext';
import { parseJsonData } from '@antimatterhq/ui/utils/parse';
import { QueryResultsDrawerContextMenu } from './QueryResultsDrawerContextMenu';
import {
  queryResultsDrawerStateAtomFamily,
  updateQueryResultsDrawerStateAtomFamily,
  activeQueryTabIdAtom,
} from '@/atoms/query/queryAtoms';

interface QueryResultsDrawerProps {
  toggleColumnVisibility?: (columnId: string) => void;
  columnVisibility?: Record<string, boolean>;
}

export function QueryResultsDrawer({
  toggleColumnVisibility,
  columnVisibility,
}: QueryResultsDrawerProps) {
  const activeTabId = useAtomValue(activeQueryTabIdAtom);
  const drawerState = useAtomValue(
    queryResultsDrawerStateAtomFamily(activeTabId ?? ''),
  );
  const updateDrawerState = useSetAtom(
    updateQueryResultsDrawerStateAtomFamily(activeTabId ?? ''),
  );

  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [selectedText, setSelectedText] = useState<string>('');
  const [propertyKey, setPropertyKey] = useState<string | null>(null);
  const { isDarkMode } = useColorMode();

  const handleClose = useCallback(() => {
    // First close the drawer (starts animation)
    updateDrawerState({
      isOpen: false,
      isExpanded: false,
    });
    // Then clear the data after animation completes
    setTimeout(() => {
      updateDrawerState({
        selectedRowData: null,
      });
    }, 300);
  }, [updateDrawerState]);

  const handleToggleExpanded = useCallback(() => {
    updateDrawerState({
      isExpanded: !drawerState.isExpanded,
    });
  }, [drawerState.isExpanded, updateDrawerState]);

  const handleContextMenu = useCallback(
    (
      path: string | null,
      _lineNumber: number,
      selected: string | null,
      event: MouseEvent,
    ) => {
      event.preventDefault();

      // Check if any menu options would be available
      const wouldHaveFilterByKey = path && !path.includes('[');
      const wouldHaveFilterBySelection = !!selected;
      const wouldHaveCopyValue = !!selected;
      const wouldHaveRemoveColumn =
        path &&
        !path.includes('.') &&
        !path.includes('[') &&
        columnVisibility &&
        columnVisibility[path] !== false;

      // Only show menu if at least one option is available
      if (
        wouldHaveFilterByKey ||
        wouldHaveFilterBySelection ||
        wouldHaveCopyValue ||
        wouldHaveRemoveColumn
      ) {
        setPropertyKey(path);
        setSelectedText(selected || '');
        setMenuPosition({ x: event.clientX, y: event.clientY });
        setMenuOpen(true);
      }
    },
    [columnVisibility],
  );

  const handleFilterByKey = useCallback(() => {
    if (propertyKey && !propertyKey.includes('[')) {
      // Note: Table filtering will be implemented when useTableColumnFiltering
      // is integrated into QueryResultsTable
    }
    setMenuOpen(false);
  }, [propertyKey]);

  const handleFilterBySelection = useCallback(() => {
    if (selectedText) {
      // Note: Table filtering will be implemented when useTableColumnFiltering
      // is integrated into QueryResultsTable
    }
    setMenuOpen(false);
  }, [selectedText]);

  const handleCopyValue = useCallback(() => {
    if (selectedText) {
      navigator.clipboard.writeText(selectedText);
    }
    setMenuOpen(false);
  }, [selectedText]);

  const handleRemoveColumn = useCallback(() => {
    if (propertyKey && toggleColumnVisibility) {
      toggleColumnVisibility(propertyKey);
    }
    setMenuOpen(false);
  }, [propertyKey, toggleColumnVisibility]);

  // Memoize computed values
  const isTopLevelProperty = useMemo(
    () =>
      propertyKey && !propertyKey.includes('.') && !propertyKey.includes('['),
    [propertyKey],
  );

  const canRemoveColumn = useMemo(
    () =>
      isTopLevelProperty &&
      columnVisibility &&
      columnVisibility[propertyKey!] !== false,
    [isTopLevelProperty, columnVisibility, propertyKey],
  );

  const canFilterByKey = useMemo(
    () => propertyKey && !propertyKey.includes('['),
    [propertyKey],
  );

  // Memoize the parsed and filtered data
  const data = useMemo(() => {
    if (!drawerState.selectedRowData) return {};
    const parsed = parseJsonData(drawerState.selectedRowData);
    return parsed;
  }, [drawerState.selectedRowData]);

  // Keep component mounted if drawer was open (for exit animation)
  if (!drawerState.isOpen && !drawerState.selectedRowData) return null;

  return (
    <>
      {/* Drawer */}
      <SideDrawer
        isOpen={drawerState.isOpen}
        onClose={handleClose}
        isExpanded={drawerState.isExpanded}
        onExpandToggle={handleToggleExpanded}
        title="Row details"
        side="right"
        width="default"
        backdrop={false}
      >
        <JsonEditor
          value={data as object | null}
          height="100%"
          theme={isDarkMode ? 'dark' : 'light'}
          onContextMenu={handleContextMenu}
          readOnly={true}
        />
      </SideDrawer>
      {/* Context menu */}
      <QueryResultsDrawerContextMenu
        open={menuOpen}
        onOpenChange={setMenuOpen}
        position={menuPosition}
        propertyKey={propertyKey}
        selectedText={selectedText}
        onFilterByKey={canFilterByKey ? handleFilterByKey : undefined}
        onFilterBySelection={handleFilterBySelection}
        onCopyValue={handleCopyValue}
        onRemoveColumn={canRemoveColumn ? handleRemoveColumn : undefined}
      />
    </>
  );
}
